import {
    ClaimApi,
    CoreFetch,
    Configuration,
    CreateClaimFacility,
    CreateClaimProvider,
    CreateClaimProviderType,
    CreateClaimGuarantor,
    CreateClaimPatient,
    CreateClaimSubscriber,
    GuarantorApi,
    PatientApi,
    PayerApi,
    PayerSubscriberApi,
    SubscriberApi,
    ProfessionalApi,
    FacilityApi,
    DocumentApi,
    ProviderCreateDocumentType,
    SupportDocumentType,
    AddressType,
    // SupportClaimDetails,
    ProviderGuarantor,
    ProviderPayer,
    SupportPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderPatient,
    ProviderApi,
    RelationshipType,
    SupportProvider,
} from '../../api';
import _ from 'lodash';
import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import { AdditionalInformation, IClaimEditorStoreConfig } from './claim-editor.types';
import { canSubmitClaim, getClaimFieldErrors, getSubmitBlockers } from './claim-editor-validation';
import {
    ClaimFieldError,
    groupErrorsBySection,
    sectionHasContent,
    toProviderList,
} from './claim-editor-rules';
import { ClaimSectionId, CLAIM_EDITOR_SECTIONS } from './claim-editor-sections';

const demoClaimEditorClient = () =>
    import('../../services/demo/claim-editor.demo').then(module => module.demoClaimEditorClient);

/**
 * This store serves both the support console and the provider app, switching API at runtime
 * on `isSupportApp`. The two payer shapes used to be structurally identical, so a single
 * `ProviderPayer` annotation covered both; they diverged when `tags` arrived with a different
 * tag enum per surface. Naming the union keeps the runtime switch honest instead of
 * asserting one surface and casting.
 */
type AnyPayer = SupportPayer | ProviderPayer;
import { CoreDocumentsStore } from '../documents.store';
import { normalizedDiagnosisCodesForPayload } from '../../utils/normalize-service-line-diagnosis-codes';

export class ClaimEditorStore {
    @observable public facility: CreateClaimFacility | null = null;
    @observable public patient: CreateClaimPatient | null = null;
    @observable public guarantor: CreateClaimGuarantor | null = null;
    @observable public providers: CreateClaimProvider[] = [];
    @observable public additionalInformation: AdditionalInformation | null = null;
    @observable public services: any[] = [];
    @observable public subscribers: CreateClaimSubscriber[] = [];
    @observable public billingProviderId: string | null = null;
    @observable public isLoading = false;
    /** Claim id whose details have been applied to the editor. Null until that load finishes. */
    @observable public loadedClaimId: string | null = null;
    /**
     * Bumped only when the editor starts pointing at genuinely different data — `resetStore`
     * ("Create Another") or a real claim switch in `loadClaimData`. Components that keep
     * mount-only local state outside Formik (e.g. SettingsForm's pre-payments/seller/documents)
     * should key off this to reinitialize instead of carrying over the previous session's values.
     */
    @observable public formSessionId: number = 0;
    @observable public claimDetails: any | null = null;
    @observable public activePayload: any = null;
    @observable public settings: {
        maximumGuarantorResponsibility?: { amount: string; currency: string };
        primaryThirdPartyAdministratorPayerId?: string;
        secondaryThirdPartyAdministratorPayerId?: string;
        tertiaryThirdPartyAdministratorPayerId?: string;
    } | null = null;
    @observable public guarantorPayments: Array<{
        collectionDate?: string;
        amount?: { amount: string; currency: string };
        memo?: string;
    }> = [];
    @observable public seller: {
        sellerId?: string;
        externalId?: string;
        first?: string;
        last?: string;
        organization?: string;
    } | null = null;
    @observable public newDocument: File | null = null;
    @observable public documents: string[] = [];
    @observable public validationSignal: number = 0;
    /** True after submit attempt or edit-mode load validation — drives banners / section status. */
    @observable public validationEvaluated: boolean = false;
    @observable public isTemplate: boolean = false;
    @observable public isCreateClaim: boolean = true;
    @observable public isControlNumberUnique: boolean | null = null;
    @observable public inactivePayerIds: string[] = [];
    @observable public inactiveProviderIds: string[] = [];
    @observable public inactiveFacilityIds: string[] = [];
    @observable private originalControlNumber: string | null = null;

    private config?: IClaimEditorStoreConfig;
    private documentsStore: CoreDocumentsStore;
    @observable private billingProvidersCache: any[] = [];
    @observable private billingProvidersLoaded: boolean = false;
    @observable private providersCache: any[] = [];
    @observable private providersLoaded: boolean = false;
    @observable private payersCache: AnyPayer[] = [];
    @observable private payersLoaded: boolean = false;
    @observable private facilitiesCache: any[] = [];
    @observable private facilitiesLoaded: boolean = false;
    private initialClaimId: string | null = null;
    private controlNumberCheckToken = 0;
    private preloadProvidersPromise: Promise<void> | null = null;
    private preloadBillingProvidersPromise: Promise<void> | null = null;
    private preloadPayersPromise: Promise<void> | null = null;
    // The unfiltered facility list (no name/id filter) is what every facility AsyncSelect's
    // `defaultOptions` fetches on mount — one top-level facility form plus one per service
    // line. Without sharing this promise they each fire an identical /facility?limit=50
    // request in parallel.
    private preloadFacilitiesPromise: Promise<any[]> | null = null;
    private loadClaimDataToken = 0;
    /**
     * Deferred PHI wipe lives on the singleton, not the React instance. `create` and
     * `:claimId/edit` are different routes, so React unmounts one editor and mounts the other
     * in the same commit — the new instance cannot cancel a timer on the old ref. A load
     * bumps this epoch so a stale timeout cannot reset create-mode over an in-flight edit.
     */
    private resetEpoch = 0;
    private pendingResetTimer: ReturnType<typeof setTimeout> | undefined;
    /**
     * Set when a claim load fails. The editor renders a full-page spinner while
     * `loadedClaimId !== claimId`, and a failed load leaves `loadedClaimId` null forever — so the
     * failure has to be observable or the screen hangs on the spinner with no way out.
     * `config.onLoadError` is not enough: neither app supplies one.
     */
    @observable public loadError: Error | null = null;
    private readonly activeFilter = 'EQ:1';

    @computed
    public get allProviders(): SupportProvider[] {
        return (this.providersCache || []) as SupportProvider[];
    }

    constructor(config?: IClaimEditorStoreConfig) {
        makeObservable(this);
        this.config = config;
        this.documentsStore = new CoreDocumentsStore({ supportGrant: (config as any)?.supportGrant });
    }

    private get isSupportApp(): boolean {
        return Boolean((this.config as any)?.supportGrant);
    }

    /** Whether this editor answers from generated data (FE-187). */
    public get isDemo(): boolean {
        return Boolean(this.config?.isDemo);
    }

    /**
     * The server client, or in demo mode the generated stand-in (FE-187), loaded on demand so the customer bundle
     * does not carry it. Every request the editor makes goes through here.
     *
     * The stand-in implements only the provider calls this store makes (`ClaimEditorDemoClient`), hence the cast. A
     * call it lacks fails visibly rather than reaching the network (NS-14).
     */
    private client = async <T>(Api: new (configuration: Configuration) => T): Promise<T> =>
        this.config?.isDemo ? ((await demoClaimEditorClient()) as unknown as T) : CoreFetch<T>(Api);

    private supportActiveListFilter(): { filterActive: string } | Record<string, never> {
        return this.isSupportApp ? { filterActive: this.activeFilter } : {};
    }

    private uniqueIds(ids: Array<string | null | undefined>): string[] {
        return Array.from(new Set(ids.filter((id): id is string => Boolean(id && String(id).trim()))));
    }

    private collectReferencedPayerIds(): string[] {
        return this.uniqueIds([
            ...(this.subscribers || []).map(subscriber => subscriber?.payerId),
            this.settings?.primaryThirdPartyAdministratorPayerId,
            this.settings?.secondaryThirdPartyAdministratorPayerId,
            this.settings?.tertiaryThirdPartyAdministratorPayerId,
        ]);
    }

    private collectReferencedProviderIds(): string[] {
        const serviceProviderIds = (this.services || []).flatMap((service: any) =>
            toProviderList(service?.providers).map((provider: any) => provider?.providerId),
        );
        return this.uniqueIds([
            this.billingProviderId,
            ...(this.providers || []).map(provider => provider?.providerId),
            ...serviceProviderIds,
        ]);
    }

    private collectReferencedFacilityIds(): string[] {
        return this.uniqueIds([
            this.facility?.facilityId,
            ...(this.services || []).map((service: any) => service?.facility?.facilityId),
        ]);
    }

    @action
    public syncInactiveCatalogIds = (): void => {
        if (this.payersLoaded) {
            const activePayerIds = new Set(this.payersCache.map(payer => payer.id).filter(Boolean) as string[]);
            this.inactivePayerIds = this.collectReferencedPayerIds().filter(id => !activePayerIds.has(id));
        }
        if (this.providersLoaded) {
            const activeProviderIds = new Set(
                this.providersCache.map(provider => provider.id).filter(Boolean) as string[],
            );
            this.inactiveProviderIds = this.collectReferencedProviderIds().filter(id => !activeProviderIds.has(id));
        }
        const referencedFacilityIds = new Set(this.collectReferencedFacilityIds());
        this.inactiveFacilityIds = this.inactiveFacilityIds.filter(id => referencedFacilityIds.has(id));
    };

    private refreshInactiveReferencedEntities = async (loadToken: number): Promise<void> => {
        const facilityIds = this.collectReferencedFacilityIds();

        // One catalog list each, then an in-memory sync — not one /payer or /provider
        // request per referenced id. Edit-load used to fan those out because mount
        // no longer preloads the caches. Facilities still have no catalog cache.
        const [, , facilityResults] = await Promise.all([
            this.preloadPayers(),
            this.preloadProviders(),
            Promise.all(
                facilityIds.map(async id => {
                    const results = await this.searchFacilities(undefined, id);
                    return {
                        id,
                        found: (results || []).some((facility: any) => facility?.id === id),
                    };
                }),
            ),
        ]);
        if (loadToken !== this.loadClaimDataToken) {
            return;
        }

        runInAction(() => {
            this.syncInactiveCatalogIds();
            this.inactiveFacilityIds = facilityResults.filter(result => !result.found).map(result => result.id);
        });
    };

    @action
    public setSupportGrant = (support: boolean | undefined): void => {
        const nextSupportGrant = Boolean(support);
        if ((this.config as any)?.supportGrant === nextSupportGrant) {
            return;
        }
        this.config = { ...(this.config as any), supportGrant: nextSupportGrant } as any;
        this.documentsStore.setSupportGrant(support);
    };
    @action
    public triggerValidation = (): void => {
        this.validationSignal = (this.validationSignal || 0) + 1;
        this.validationEvaluated = true;
    };

    private getValidationData() {
        const payload = this.buildPayload() || {};
        return {
            patient: payload.patient,
            guarantor: payload.guarantor,
            billingProviderId: payload.billingProviderId ?? this.billingProviderId,
            services: payload.services ?? this.services,
            providers: payload.providers ?? this.providers,
            additionalInformation: this.additionalInformation,
            guarantorPayments: payload.guarantorPayments ?? this.guarantorPayments,
            seller: payload.seller ?? this.seller,
            facility: payload.facility ?? this.facility,
            subscribers: payload.subscribers ?? this.subscribers,
            inactivePayerIds: this.inactivePayerIds,
            inactiveProviderIds: this.inactiveProviderIds,
            inactiveFacilityIds: this.inactiveFacilityIds,
            primaryThirdPartyAdministratorPayerId: this.settings?.primaryThirdPartyAdministratorPayerId,
            secondaryThirdPartyAdministratorPayerId: this.settings?.secondaryThirdPartyAdministratorPayerId,
            tertiaryThirdPartyAdministratorPayerId: this.settings?.tertiaryThirdPartyAdministratorPayerId,
        };
    }

    @computed
    public get fieldErrors(): ClaimFieldError[] {
        return getClaimFieldErrors(this.getValidationData());
    }

    @computed
    public get errorsBySection(): Partial<Record<ClaimSectionId, ClaimFieldError[]>> {
        return groupErrorsBySection(this.fieldErrors);
    }

    @computed
    public get sectionStatuses(): Array<{
        id: ClaimSectionId;
        title: string;
        navLabel: string;
        icon: import('./claim-editor-sections').ClaimSectionIcon;
        disabledKey?: string;
        errorCount: number;
        done: boolean;
        hasContent: boolean;
    }> {
        const data = this.getValidationData();
        const bySection = this.errorsBySection;
        return CLAIM_EDITOR_SECTIONS.map(section => {
            const errorCount = bySection[section.id]?.length || 0;
            const hasContent = sectionHasContent(section.id, data);
            const done = this.validationEvaluated && hasContent && errorCount === 0;
            return {
                ...section,
                errorCount,
                done,
                hasContent,
            };
        });
    }

    @computed
    public get canSubmit(): boolean {
        return canSubmitClaim(this.getValidationData());
    }

    public getSubmitBlockers(): string[] {
        return getSubmitBlockers(this.getValidationData());
    }

    public isPayerIdActive = (payerId?: string | null): boolean => {
        if (!payerId) return true;
        if (!this.payersLoaded) return !this.inactivePayerIds.includes(payerId);
        return this.payersCache.some(payer => payer.id === payerId);
    };

    public isProviderIdActive = (providerId?: string | null): boolean => {
        if (!providerId) return true;
        if (!this.providersLoaded) return !this.inactiveProviderIds.includes(providerId);
        return this.providersCache.some(provider => provider.id === providerId);
    };

    public isFacilityIdActive = (facilityId?: string | null): boolean => {
        if (!facilityId) return true;
        return !this.inactiveFacilityIds.includes(facilityId);
    };

    @action
    public markFacilityIdActive = (facilityId?: string | null): void => {
        if (!facilityId) return;
        if (!this.inactiveFacilityIds.includes(facilityId)) return;
        this.inactiveFacilityIds = this.inactiveFacilityIds.filter(id => id !== facilityId);
    };

    private deepClean = (input: any): any => {
        if (Array.isArray(input)) {
            const arr = input
                .map(this.deepClean)
                .filter(v => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === ''))
                .filter(v => !(Array.isArray(v) && v.length === 0))
                .filter(v => !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0));
            return arr;
        }
        if (input && typeof input === 'object') {
            const out: any = {};
            Object.keys(input).forEach(k => {
                const v = this.deepClean((input as any)[k]);
                const isEmptyString = typeof v === 'string' && v.trim() === '';
                const isNullish = v === undefined || v === null;
                const isEmptyArray = Array.isArray(v) && v.length === 0;
                const isEmptyObject = v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0;
                if (!isNullish && !isEmptyString && !isEmptyArray && !isEmptyObject) {
                    out[k] = v;
                }
            });
            return out;
        }
        if (typeof input === 'string') {
            return input.trim();
        }
        return input;
    };

    /**
     * Abandon whatever load is in flight.
     *
     * Every stage of `loadClaimData` re-checks its token, so bumping it here makes a late response
     * a no-op. The editor calls this on unmount BEFORE the deferred `resetStore`: this store is a
     * singleton, and without it a response arriving after the user moved to a different claim (or
     * to create) repopulated the new editor with the previous claim's data — a PHI leak across
     * screens, not just a stale render.
     */
    @action
    public cancelActiveLoad = (): void => {
        this.loadClaimDataToken += 1;
        this.isLoading = false;
    };

    private cancelPendingReset = (): void => {
        if (this.pendingResetTimer !== undefined) {
            clearTimeout(this.pendingResetTimer);
            this.pendingResetTimer = undefined;
        }
    };

    /**
     * A live editor session now owns this singleton. Any deferred wipe scheduled by the
     * previous instance must not run — including the same-claim early-return path, which
     * never reaches the fetch.
     */
    private adoptEditorSession = (): void => {
        this.resetEpoch += 1;
        this.cancelPendingReset();
    };

    /**
     * PHI wipe on a timeout so a remount of the same editor (same claim) does not clear the
     * payload and put the full-page spinner back up. The timer is store-owned so a newly
     * mounted editor can invalidate it; the React ref cannot.
     */
    @action
    public scheduleDeferredReset = (): void => {
        this.cancelPendingReset();
        const epoch = this.resetEpoch;
        this.pendingResetTimer = setTimeout(() => {
            this.pendingResetTimer = undefined;
            if (epoch !== this.resetEpoch) {
                return;
            }
            this.resetStore();
        }, 0);
    };

    @action
    public loadClaimData = async (claimId: string, isTemplate = false): Promise<void> => {
        this.adoptEditorSession();
        const sameClaimAlreadyLoaded =
            this.loadedClaimId === claimId &&
            this.isTemplate === Boolean(isTemplate) &&
            Boolean(this.activePayload) &&
            !this.isLoading;
        if (sameClaimAlreadyLoaded) {
            return;
        }

        // Template mode counts as a switch. Opening the SAME claim with ?template=true otherwise
        // left the original payload rendered and editable while the template request was in
        // flight — `isCreateClaim` had already flipped and the loader stayed hidden because
        // `loadedClaimId` still matched.
        const switchingClaim = this.loadedClaimId !== claimId || this.isTemplate !== Boolean(isTemplate);
        const currentLoadToken = ++this.loadClaimDataToken;
        runInAction(() => {
            this.loadError = null;
            this.isTemplate = Boolean(isTemplate);
            this.initialClaimId = claimId || null;
            this.isCreateClaim = !this.initialClaimId || this.isTemplate;
            this.originalControlNumber = null;
            if (switchingClaim || !this.activePayload) {
                this.isLoading = true;
                this.loadedClaimId = null;
                this.activePayload = null;
                this.formSessionId += 1;
            }
        });

        try {
            // Step 1: Fetch claim details
            const details = await (await this.client<ClaimApi>(ClaimApi))[
                this.isSupportApp ? 'getSupportClaimDetails' : 'getProviderClaimDetails'
            ]({
                id: claimId,
            } as any);

            if (currentLoadToken !== this.loadClaimDataToken) {
                return;
            }

            runInAction(() => {
                this.claimDetails = details;
            });

            // Use HL7-derived payload as the only source
            let activePayload = this.determineActivePayload(isTemplate);

            // Capture additional information fields BEFORE omitting for templates
            const capturedAdditionalInfo = activePayload
                ? {
                      controlNumber: activePayload.controlNumber || '',
                      claimNarrative: activePayload.claimNarrative || '',
                      admissionDateTime: this.combineDateAndTime(
                          activePayload.admissionDate,
                          activePayload.admissionTime,
                      ),
                      dischargeDateTime: this.combineDateAndTime(
                          activePayload.dischargeDate,
                          activePayload.dischargeTime,
                      ),
                      primaryPayerAuthorizationNumber: activePayload.primaryPayerAuthorizationNumber || '',
                      secondaryPayerAuthorizationNumber: activePayload.secondaryPayerAuthorizationNumber || '',
                      tertiaryPayerAuthorizationNumber: activePayload.tertiaryPayerAuthorizationNumber || '',
                      cliaNumber: activePayload.cliaNumber || '',
                      otherDateType: activePayload.otherDateType || '',
                      fromOtherDate: activePayload.fromOtherDate || '',
                      throughOtherDate: activePayload.throughOtherDate || '',
                  }
                : null;

            if (isTemplate) {
                // Remove fields to create new claim from template
                activePayload = _.omit(activePayload, ['controlNumber', 'claimId']);
            }

            if (currentLoadToken !== this.loadClaimDataToken) {
                return;
            }

            runInAction(() => {
                this.activePayload = activePayload;

                // Extract facility data from active payload
                if (activePayload?.facility) {
                    this.facility = activePayload.facility;
                }

                // Extract patient from active payload
                if (activePayload?.patient) {
                    this.patient = activePayload.patient as CreateClaimPatient;
                }

                // Extract guarantor from active payload
                if (activePayload?.guarantor) {
                    this.guarantor = activePayload.guarantor as CreateClaimGuarantor;
                }

                // Extract services / subscribers / billing provider
                this.services = Array.isArray(activePayload?.services)
                    ? activePayload.services.map((service: any) => {
                          const providers = Array.isArray(service?.providers)
                              ? service.providers.map((p: any) => ({
                                    ...p,
                                    phone: p?.phone || p?.provider?.phone || undefined,
                                    fax: p?.fax || p?.provider?.fax || undefined,
                                }))
                              : service?.providers;
                          return { ...service, providers };
                      })
                    : [];
                this.subscribers = Array.isArray(activePayload?.subscribers) ? activePayload.subscribers : [];
                this.billingProviderId = (activePayload?.billingProviderId as any) || null;

                // Extract providers from active payload if present
                try {
                    const providersData: any[] = Array.isArray(activePayload?.providers) ? activePayload.providers : [];
                    if (providersData && providersData.length > 0) {
                        this.providers = providersData
                            .filter(p => p?.type !== CreateClaimProviderType.ORDERING)
                            .map(
                                p =>
                                    ({
                                        organization: p.organization || p.provider?.organization || undefined,
                                        first: p.first || p.provider?.first || undefined,
                                        middle: p.middle || p.provider?.middle || undefined,
                                        last: p.last || p.provider?.last || undefined,
                                        suffix: p.suffix || p.provider?.suffix || undefined,
                                        npi: p.npi || p.provider?.npi || undefined,
                                        phone: p.phone || p.provider?.phone || undefined,
                                        fax: p.fax || p.provider?.fax || undefined,
                                        providerId: p.providerId || p.provider?.id || undefined,
                                        type: p.type,
                                    }) as CreateClaimProvider,
                            );
                    }
                } catch (e) {
                    // noop
                }

                if (activePayload) {
                    this.settings = {
                        maximumGuarantorResponsibility: activePayload.maximumGuarantorResponsibility || undefined,
                        primaryThirdPartyAdministratorPayerId:
                            activePayload.primaryThirdPartyAdministratorPayerId || undefined,
                        secondaryThirdPartyAdministratorPayerId:
                            activePayload.secondaryThirdPartyAdministratorPayerId || undefined,
                        tertiaryThirdPartyAdministratorPayerId:
                            activePayload.tertiaryThirdPartyAdministratorPayerId || undefined,
                    };
                }

                if (activePayload?.guarantorPayments && Array.isArray(activePayload.guarantorPayments)) {
                    this.guarantorPayments = activePayload.guarantorPayments;
                }

                if (activePayload?.seller) {
                    const sellerPayload: any = activePayload.seller;
                    this.seller = {
                        sellerId: sellerPayload?.sellerId,
                        ...sellerPayload,
                    };
                }

                // Extract additional information from captured values (before template omission)
                // For templates, controlNumber and claimId will be empty/undefined (as intended for new claims)
                if (capturedAdditionalInfo) {
                    this.additionalInformation = isTemplate
                        ? { ...capturedAdditionalInfo, controlNumber: '' }
                        : capturedAdditionalInfo;
                } else {
                    this.additionalInformation = null;
                }

                const normalizedOriginal = capturedAdditionalInfo?.controlNumber
                    ? String(capturedAdditionalInfo.controlNumber).trim()
                    : null;
                this.originalControlNumber = normalizedOriginal;
                this.isControlNumberUnique = normalizedOriginal && !this.isTemplate ? false : null;
                this.updateIsCreateClaimStatus(this.additionalInformation?.controlNumber);
                this.loadedClaimId = claimId;
                this.isLoading = false;
            });

            if (currentLoadToken !== this.loadClaimDataToken) {
                return;
            }

            void this.refreshInactiveReferencedEntities(currentLoadToken)
                .then(() => {
                    if (currentLoadToken !== this.loadClaimDataToken) {
                        return;
                    }
                    if (this.validationEvaluated) {
                        this.triggerValidation();
                    }
                })
                .catch(error => {
                    console.warn('refreshInactiveReferencedEntities failed', error);
                });
        } catch (error) {
            if (currentLoadToken !== this.loadClaimDataToken) {
                return;
            }
            console.error('Failed to load claim data:', error);
            runInAction(() => {
                this.loadError = error instanceof Error ? error : new Error('Failed to load claim data');
            });

            // Call app-specific error handler if provided
            if (this.config?.onLoadError && error instanceof Error) {
                this.config.onLoadError(error);
            }
        } finally {
            if (currentLoadToken === this.loadClaimDataToken) {
                runInAction(() => {
                    this.isLoading = false;
                });
            }
        }
    };

    private determineActivePayload(isTemplate: boolean): any {
        const sortedHl7s = this.claimDetails?.hl7s?.sort(
            (a: any, b: any) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime(),
        );

        const lastRelevant = sortedHl7s?.find(
            (hl7: any) => hl7?.providerProfessional || hl7?.supportProfessional || hl7?.professional,
        );

        const result =
            lastRelevant?.providerProfessional ?? lastRelevant?.supportProfessional ?? lastRelevant?.professional;

        // Fallback: if no professional payload found, return an empty payload to behave like create-claim
        const fallback: any = { services: [], subscribers: [], providers: [] };

        // Remove fields to create new claim from template
        if (isTemplate) {
            return _.omit(result || fallback, ['controlNumber', 'claimId']);
        }

        return result || fallback;
    }

    @action
    public updateFacility = (facilityData: CreateClaimFacility | null): void => {
        this.facility = facilityData;
        this.syncInactiveCatalogIds();
    };

    @action
    public updatePatient = (patientData: CreateClaimPatient | null): void => {
        if (!patientData) {
            this.patient = null;
            return;
        }

        const hasAddresses = Object.prototype.hasOwnProperty.call(patientData, 'addresses');
        const hasPhones = Object.prototype.hasOwnProperty.call(patientData, 'phones');
        const hasEmails = Object.prototype.hasOwnProperty.call(patientData, 'emails');

        this.patient = {
            ...(this.patient || {}),
            ...patientData,
            // If the property is present on patientData (even as undefined), overwrite;
            // otherwise, preserve the existing value.
            addresses: hasAddresses ? patientData.addresses : this.patient?.addresses,
            phones: hasPhones ? patientData.phones : this.patient?.phones,
            emails: hasEmails ? patientData.emails : this.patient?.emails,
        };
    };

    @action
    public updateGuarantor = (guarantorData: CreateClaimGuarantor | null): void => {
        this.guarantor = guarantorData;
    };

    @action
    public copyGuarantorFromPatient = (): void => {
        const patient = this.patient;
        if (!patient) return;

        const firstPhone = patient.phones?.find(phone => !!phone);
        const firstEmail = patient.emails?.find(email => !!email);

        const guarantor: CreateClaimGuarantor = {
            first: patient.first,
            middle: patient.middle,
            suffix: patient.suffix,
            last: patient.last,
            dob: patient.dob,
            phone: firstPhone || undefined,
            email: firstEmail || undefined,
            addresses: patient.addresses?.map(addr => ({ ...addr })) ?? [],
        } as any;
        this.guarantor = guarantor;
    };

    @action
    public updateProviders = (providers: CreateClaimProvider[]): void => {
        this.providers = providers;
        this.syncInactiveCatalogIds();
    };

    @action
    public deleteFacility = (): void => {
        this.facility = null;
        this.syncInactiveCatalogIds();
    };

    @action
    public deletePatient = (): void => {
        this.patient = null;
    };

    @action
    public updateAdditionalInformation = (informationData: AdditionalInformation | null): void => {
        this.additionalInformation = informationData;
        this.updateIsCreateClaimStatus(informationData?.controlNumber);
    };

    @action
    public updateServices = (servicesData: any[] | null): void => {
        const incoming: any[] = Array.isArray(servicesData) ? servicesData : [];
        const previous: any[] = Array.isArray(this.services) ? this.services : [];

        // Preserve nested fields on existing service lines unless explicitly provided
        const merged = incoming.map((svc, index) => {
            const nextSvc: any = { ...(svc || {}) };
            const prevSvc: any = previous[index] || {};

            if (typeof nextSvc.providers === 'undefined' && typeof prevSvc.providers !== 'undefined') {
                nextSvc.providers = prevSvc.providers;
            }
            if (typeof nextSvc.facility === 'undefined' && typeof prevSvc.facility !== 'undefined') {
                nextSvc.facility = prevSvc.facility;
            }
            // Preserve drug object if not explicitly provided
            if (typeof nextSvc.drug === 'undefined' && typeof prevSvc.drug !== 'undefined') {
                nextSvc.drug = prevSvc.drug;
            }
            return nextSvc;
        });
        this.services = merged;
        this.syncInactiveCatalogIds();
    };

    /**
     * Update one service line (partial merge) without clobbering nested fields.
     *
     * Use this for granular, debounced edits inside a service form. Only pass the flat
     * properties being edited; nested structures such as providers/facility will be
     * preserved unless explicitly provided.
     */
    @action
    public updateServiceLine = (serviceIndex: number, serviceData: any): void => {
        const next = [...(this.services || [])];
        const prevSvc: any = next[serviceIndex] || {};
        // Preserve nested fields (providers, facility, drug) when updating main service data
        const updated: any = {
            ...prevSvc,
            ...serviceData,
            providers: serviceData.providers !== undefined ? serviceData.providers : prevSvc.providers,
            facility: serviceData.facility !== undefined ? serviceData.facility : prevSvc.facility,
            drug: serviceData.drug !== undefined ? serviceData.drug : prevSvc.drug,
        };
        next[serviceIndex] = updated;
        this.services = next;
    };

    /**
     * Update only the facility of a service line.
     *
     * Use this from the Service Line Facility form so facility edits are isolated and not
     * affected by concurrent saves from the main service form.
     */
    @action
    public updateServiceLineFacility = (serviceIndex: number, facility: any | null): void => {
        const next = [...(this.services || [])];
        const svc = { ...(next[serviceIndex] || {}) };
        (svc as any).facility = facility || null;
        next[serviceIndex] = svc;
        this.services = next;
        this.syncInactiveCatalogIds();
    };

    @action
    public deleteServiceLineFacility = (serviceIndex: number): void => {
        this.updateServiceLineFacility(serviceIndex, null);
    };

    @action
    public updateServiceLineProvider = (
        serviceIndex: number,
        type: 'RENDERING' | 'REFERRING' | 'SUPERVISING' | 'ORDERING',
        provider: CreateClaimProvider | null,
    ): void => {
        const next = [...(this.services || [])];
        const svc: any = { ...(next[serviceIndex] || {}) };
        const providers: any[] = [...toProviderList(svc.providers)];
        const normalizedType = String(type).toUpperCase();
        // Remove any existing provider of this type (normalize both sides)
        const filtered = providers.filter(p => String(p?.type).toUpperCase() !== normalizedType);
        // Optionally add/update this type
        if (provider) {
            filtered.push({ ...provider, type: normalizedType });
        }

        svc.providers = filtered;
        next[serviceIndex] = svc;
        this.services = next;
        this.syncInactiveCatalogIds();
    };

    @action
    public deleteServiceLineProvider = (
        serviceIndex: number,
        type: 'RENDERING' | 'REFERRING' | 'SUPERVISING' | 'ORDERING',
    ): void => {
        this.updateServiceLineProvider(serviceIndex, type, null);
    };

    /**
     * Update only the drug information of a service line.
     *
     * Use this from the Service Line Drug form so drug edits are isolated and not
     * affected by concurrent saves from the main service form.
     */
    @action
    public updateServiceLineDrug = (
        serviceIndex: number,
        drug: { x12NationalDrugCode?: string; x12DrugQuantity?: string; x12DrugMeasurement?: string } | null,
    ): void => {
        const next = [...(this.services || [])];
        const svc: any = { ...(next[serviceIndex] || {}) };
        (svc as any).drug = drug || null;
        next[serviceIndex] = svc;
        this.services = next;
    };

    @action
    public deleteServiceLineDrug = (serviceIndex: number): void => {
        this.updateServiceLineDrug(serviceIndex, null);
    };

    /**
     * Copy a service line and insert it right after the current one.
     * Generates a new service-line control number for the copied line.
     */
    @action
    public copyServiceLine = (serviceIndex: number): void => {
        const current = Array.isArray(this.services) ? this.services : [];
        if (serviceIndex < 0 || serviceIndex >= current.length) return;

        const sourceService = current[serviceIndex];
        if (!sourceService) return;

        // Deep copy the service line
        const copiedService: any = {
            ...sourceService,
        };

        // Deep copy nested arrays/objects
        if (Array.isArray(sourceService.diagnosisCodes)) {
            copiedService.diagnosisCodes = [...sourceService.diagnosisCodes];
        }
        if (Array.isArray(sourceService.procedureModifierCodes)) {
            copiedService.procedureModifierCodes = [...sourceService.procedureModifierCodes];
        }
        if (Array.isArray(sourceService.providers)) {
            copiedService.providers = sourceService.providers.map((p: any) => ({ ...p }));
        }
        if (sourceService.facility) {
            const facilityCopy: any = { ...sourceService.facility };
            // Deep copy facility addresses if present
            if (Array.isArray(sourceService.facility.addresses)) {
                facilityCopy.addresses = sourceService.facility.addresses.map((addr: any) => ({ ...addr }));
            }
            copiedService.facility = facilityCopy;
        }

        // Insert the copied service right after the current one
        const next = [...current];
        next.splice(serviceIndex + 1, 0, copiedService);
        this.services = next;
        this.syncInactiveCatalogIds();
    };

    @action
    public updateSubscribers = (subscribersData: CreateClaimSubscriber[] | null): void => {
        this.subscribers = subscribersData || [];
        this.syncInactiveCatalogIds();
    };

    @action
    public copySubscriberFromPatient = (index: number): void => {
        const patient = this.patient;
        if (!patient || index < 0) return;

        const subscribers: CreateClaimSubscriber[] = Array.isArray(this.subscribers) ? [...this.subscribers] : [];

        const existing = subscribers[index] || ({} as CreateClaimSubscriber);

        subscribers[index] = {
            ...existing,
            // The patient is a different record from whoever `subscriberId` pointed at, and
            // the API ignores every other subscriber field while that id is present — so
            // spreading `existing` over it transmitted the OLD subscriber under the new name.
            subscriberId: undefined,
            first: patient.first,
            middle: patient.middle,
            suffix: patient.suffix,
            last: patient.last,
            dob: patient.dob,
            sex: patient.sex as any,
            relationshipToPatient: RelationshipType.SELF as any,
            phone: patient.phones?.[0],
            ssn: (patient as any).ssn || existing.ssn,
            addresses: patient.addresses,
        };

        this.subscribers = subscribers;
    };

    @action
    public updateBillingProviderId = (id: string | null): void => {
        this.billingProviderId = id;
        this.syncInactiveCatalogIds();
    };

    @action
    public deleteBillingProvider = (): void => {
        this.billingProviderId = null;
        this.syncInactiveCatalogIds();
    };

    @action
    public updateSettings = (
        settingsData: {
            maximumGuarantorResponsibility?: { amount: string; currency: string };
            primaryThirdPartyAdministratorPayerId?: string;
            secondaryThirdPartyAdministratorPayerId?: string;
            tertiaryThirdPartyAdministratorPayerId?: string;
        } | null,
    ): void => {
        this.settings = settingsData;
        this.syncInactiveCatalogIds();
    };

    @action
    public updateguarantorPayments = (
        payments: Array<{
            collectionDate?: string;
            amount?: { amount: string; currency: string };
            memo?: string;
        }> | null,
    ): void => {
        this.guarantorPayments = payments || [];
    };

    @action
    public updateSeller = (
        sellerData: {
            sellerId?: string;
            externalId?: string;
            first?: string;
            last?: string;
            organization?: string;
        } | null,
    ): void => {
        this.seller = sellerData;
    };

    @action
    public updateNewDocument = (document: File | null): void => {
        this.newDocument = document;
    };

    @action
    public updateDocuments = (documentIds: string[]): void => {
        this.documents = documentIds;
    };

    public uploadDocument = async (file: File): Promise<string | null> => {
        try {
            const documentId = await this.documentsStore.uploadDocumentFile(file, {
                documentType: this.isSupportApp
                    ? SupportDocumentType.MESSAGEDOCUMENT
                    : ProviderCreateDocumentType.MESSAGEDOCUMENT,
            });

            // Update documents array
            runInAction(() => {
                this.documents = [...this.documents, documentId];
            });

            return documentId;
        } catch (error) {
            console.error('Failed to upload document:', error);
            return null;
        }
    };

    public fetchAllPayers = async (): Promise<AnyPayer[]> => {
        try {
            const api = await this.client<PayerApi>(PayerApi);
            const limit = 100;
            let allData: AnyPayer[] = [];

            // Fetch first page to get total count
            const firstResponse = await (this.isSupportApp
                ? api.listSupportPayers({
                      limit,
                      offset: 0,
                      order: 'ASCENDING' as any,
                      sort: 'NAME' as any,
                      ...this.supportActiveListFilter(),
                  } as any)
                : api.listProviderPayers({
                      limit,
                      offset: 0,
                      order: 'ASCENDING' as any,
                      sort: 'NAME' as any,
                  } as any));

            // Note: The pagination-total header would need to be accessed from the response metadata
            // For now, we'll fetch pages until we get less than the limit
            allData = allData.concat(firstResponse || []);

            // Continue fetching if we got a full page
            let page = 1;
            let lastLength = (firstResponse || []).length;
            while (lastLength === limit) {
                const offset = page * limit;
                const response = await (this.isSupportApp
                    ? api.listSupportPayers({
                          limit,
                          offset,
                          order: 'ASCENDING' as any,
                          sort: 'NAME' as any,
                          ...this.supportActiveListFilter(),
                      } as any)
                    : api.listProviderPayers({
                          limit,
                          offset,
                          order: 'ASCENDING' as any,
                          sort: 'NAME' as any,
                      } as any));

                if (!response || response.length === 0) break;
                allData = allData.concat(response);
                lastLength = response.length;
                page++;

                // Safety check to prevent infinite loop
                if (page > 100) break; // Max 10,000 records
            }

            // Deduplicate by id
            const deduplicatedData: AnyPayer[] = [];
            const ids = new Set<string>();
            allData.forEach(item => {
                if (item.id && !ids.has(item.id)) {
                    ids.add(item.id);
                    deduplicatedData.push(item);
                }
            });

            return deduplicatedData;
        } catch (e) {
            console.warn('fetchAllPayers failed', e);
            return [];
        }
    };

    private formatDateToString = (date: any): string | undefined => {
        if (!date) return undefined;

        // If already a string, return as-is
        if (typeof date === 'string') return date;

        // If array format [year, month, day], convert to string
        if (Array.isArray(date) && date.length === 3) {
            const [year, month, day] = date;
            const paddedMonth = String(month).padStart(2, '0');
            const paddedDay = String(day).padStart(2, '0');
            return `${year}-${paddedMonth}-${paddedDay}`;
        }

        return undefined;
    };

    /** Normalize to OpenAPI HH:mm:ss (HTML time inputs may omit seconds). */
    private formatTimeToString = (time: any): string | undefined => {
        if (!time) return undefined;
        if (typeof time !== 'string') return undefined;

        const trimmed = time.trim();
        if (!trimmed) return undefined;

        const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
        if (!match) return trimmed;

        const hours = match[1].padStart(2, '0');
        const minutes = match[2];
        const seconds = match[3] ?? '00';
        return `${hours}:${minutes}:${seconds}`;
    };

    /** Combine API date + time into a wall-clock form value (no timezone conversion). */
    private combineDateAndTime = (date?: string, time?: string): string => {
        const dateOnly = this.formatDateToString(date);
        if (!dateOnly) return '';
        const timeOnly = this.formatTimeToString(time);
        return timeOnly ? `${dateOnly} ${timeOnly}` : dateOnly;
    };

    /** Split wall-clock form value into OpenAPI admissionDate / admissionTime fields. */
    private splitDateAndTime = (
        dateTime?: string,
    ): { date?: string; time?: string } => {
        if (!dateTime || typeof dateTime !== 'string') return {};
        const trimmed = dateTime.trim();
        if (!trimmed) return {};

        const [datePart, timePart] = trimmed.split(/\s+/);
        return {
            date: this.formatDateToString(datePart),
            time: this.formatTimeToString(timePart),
        };
    };

    private isAddressNotEmpty = (addr: any): boolean => {
        if (!addr || typeof addr !== 'object') return false;
        return !!(
            (addr.name && String(addr.name).trim()) ||
            (Array.isArray(addr.lines) && addr.lines.some((line: any) => line && String(line).trim())) ||
            (addr.city && String(addr.city).trim()) ||
            addr.state ||
            (addr.postal && String(addr.postal).trim()) ||
            addr.country
        );
    };

    private addMailingTypeToAddresses = (addresses: any[] | undefined): any[] | undefined => {
        if (!Array.isArray(addresses) || addresses.length === 0) return addresses;
        return addresses.map((addr: any) => {
            if (this.isAddressNotEmpty(addr)) {
                const country = typeof addr.country === 'string' ? addr.country.trim() : addr.country;
                return {
                    ...addr,
                    type: AddressType.MAILING,
                    country: country ? country : 'US',
                };
            }
            return addr;
        });
    };

    private buildPayload = (): any => {
        const info = this.additionalInformation || ({} as any);
        const settings = this.settings || ({} as any);

        const services = (this.services || []).map((svc: any) => {
            const pos = svc.placeOfService;
            const mods: string[] = svc.procedureModifierCodes
                ? svc.procedureModifierCodes
                : [
                      svc.procedureModifier1,
                      svc.procedureModifier2,
                      svc.procedureModifier3,
                      svc.procedureModifier4,
                  ].filter(Boolean);

            // Process facility addresses in service
            const facility = svc.facility
                ? {
                      ...svc.facility,
                      addresses: this.addMailingTypeToAddresses(svc.facility.addresses),
                  }
                : svc.facility;

            // Drug information is already in TemplateDrugInformation format
            // Clean the drug object to remove empty fields but preserve the object if it has any valid fields
            let drug: any = undefined;
            if (svc.drug) {
                const cleanedDrug = this.deepClean(svc.drug);
                // Only include drug if it has at least one field after cleaning
                if (cleanedDrug && typeof cleanedDrug === 'object' && Object.keys(cleanedDrug).length > 0) {
                    drug = cleanedDrug;
                }
            }

            const servicePayload: any = {
                placeOfService: pos,
                fromDateOfService: this.formatDateToString(svc.fromDateOfService),
                throughDateOfService: this.formatDateToString(svc.throughDateOfService),
                diagnosisCodes: normalizedDiagnosisCodesForPayload(svc.diagnosisCodes),
                procedureCode: svc.procedureCode,
                procedureModifierCodes: mods && mods.length ? mods : undefined,
                units: svc.units,
                emergency: svc.emergency,
                cliaNumber: svc.cliaNumber,
                primaryPayerAuthorizationNumber: svc.primaryPayerAuthorizationNumber,
                secondaryPayerAuthorizationNumber: svc.secondaryPayerAuthorizationNumber,
                tertiaryPayerAuthorizationNumber: svc.tertiaryPayerAuthorizationNumber,
                narrative: svc.narrative,
                providers: svc.providers,
                facility,
            };

            // Explicitly add drug if it exists (don't let deepClean remove it if it's a valid object)
            if (drug !== undefined) {
                servicePayload.drug = drug;
            }

            return this.deepClean(servicePayload);
        });

        // Process addresses and dates for patient, guarantor, subscribers, and facility
        const patient = this.patient
            ? {
                  ...this.patient,
                  dob: this.formatDateToString(this.patient.dob),
                  addresses: this.addMailingTypeToAddresses(this.patient.addresses),
              }
            : this.patient;

        const guarantor = this.guarantor
            ? {
                  ...this.guarantor,
                  dob: this.formatDateToString(this.guarantor.dob),
                  addresses: this.addMailingTypeToAddresses(this.guarantor.addresses),
              }
            : this.guarantor;

        const subscribers =
            Array.isArray(this.subscribers) && this.subscribers.length > 0
                ? this.subscribers.map((sub: any) => ({
                      ...sub,
                      dob: this.formatDateToString(sub.dob),
                      addresses: this.addMailingTypeToAddresses(sub.addresses),
                  }))
                : this.subscribers;

        const facility = this.facility
            ? {
                  ...this.facility,
                  addresses: this.addMailingTypeToAddresses(this.facility.addresses),
              }
            : this.facility;

        // Format dates in guarantor payments
        const guarantorPayments =
            Array.isArray(this.guarantorPayments) && this.guarantorPayments.length > 0
                ? this.guarantorPayments.map((payment: any) => ({
                      ...payment,
                      collectionDate: this.formatDateToString(payment.collectionDate),
                  }))
                : this.guarantorPayments;

        const raw = {
            // ProviderCreateClaimTypePayload
            controlNumber: info.controlNumber,
            billingProviderId: this.billingProviderId || undefined,
            maximumGuarantorResponsibility: settings.maximumGuarantorResponsibility || undefined,
            guarantorPayments,
            claimNarrative: info.claimNarrative,
            cliaNumber: info.cliaNumber,
            primaryPayerAuthorizationNumber: info.primaryPayerAuthorizationNumber,
            secondaryPayerAuthorizationNumber: info.secondaryPayerAuthorizationNumber,
            tertiaryPayerAuthorizationNumber: info.tertiaryPayerAuthorizationNumber,
            primaryThirdPartyAdministratorPayerId: settings.primaryThirdPartyAdministratorPayerId,
            secondaryThirdPartyAdministratorPayerId: settings.secondaryThirdPartyAdministratorPayerId,
            tertiaryThirdPartyAdministratorPayerId: settings.tertiaryThirdPartyAdministratorPayerId,
            subscribers,
            patient,
            guarantor,
            providers: this.providers.filter(p => p.type !== CreateClaimProviderType.ORDERING),
            seller: this.seller || undefined,
            facility,
            documents: this.documents && this.documents.length > 0 ? this.documents : undefined,
            // ProviderCreateClaimProfessionalPayload
            ...(() => {
                const admission = this.splitDateAndTime(info.admissionDateTime);
                const discharge = this.splitDateAndTime(info.dischargeDateTime);
                return {
                    admissionDate: admission.date,
                    admissionTime: admission.time,
                    dischargeDate: discharge.date,
                    dischargeTime: discharge.time,
                };
            })(),
            otherDateType: (info.otherDateType as any) || undefined,
            fromOtherDate: this.formatDateToString(info.fromOtherDate),
            throughOtherDate: this.formatDateToString(info.throughOtherDate),
            services,
        };
        return this.deepClean(raw);
    };

    @action
    public submitClaim = async (): Promise<string | null> => {
        const payload = this.buildPayload();
        if (!this.canSubmit) {
            const blockers = this.getSubmitBlockers();
            const message =
                blockers && blockers.length
                    ? `Cannot submit:\n- ${blockers.join('\n- ')}`
                    : 'Please complete all required fields before submitting.';
            throw new Error(message);
        }

        if (this.config?.additionalValidation && !this.config.additionalValidation(payload)) {
            throw new Error('Additional validation failed');
        }

        try {
            this.isLoading = true;
            const api = await this.client<ClaimApi>(ClaimApi);
            const result = this.isSupportApp
                ? await api.createSupportClaimProfessional({
                      supportCreateClaimProfessionalPayload: payload,
                  } as any)
                : await api.createProviderClaimProfessional({
                      providerCreateClaimProfessionalPayload: payload,
                  } as any);
            const id = (result as any)?.id || null;

            if (this.config?.onSubmit) {
                await this.config.onSubmit(payload);
            }

            return id;
        } catch (e) {
            console.error('submitClaim failed', e);
            return null;
        } finally {
            this.isLoading = false;
        }
    };

    @action
    public resetStore = (): void => {
        this.resetEpoch += 1;
        this.formSessionId += 1;
        this.cancelPendingReset();
        this.facility = null;
        this.patient = null;
        this.guarantor = null;
        this.providers = [];
        this.additionalInformation = null;
        this.services = [];
        this.subscribers = [];
        this.billingProviderId = null;
        this.isLoading = false;
        this.loadedClaimId = null;
        this.claimDetails = null;
        this.activePayload = null;
        this.settings = null;
        this.guarantorPayments = [];
        this.seller = null;
        this.newDocument = null;
        this.documents = [];
        this.isTemplate = false;
        this.isCreateClaim = true;
        this.originalControlNumber = null;
        this.initialClaimId = null;
        this.isControlNumberUnique = null;
        this.controlNumberCheckToken = 0;
        this.payersCache = [];
        this.payersLoaded = false;
        this.providersCache = [];
        this.providersLoaded = false;
        this.facilitiesCache = [];
        this.facilitiesLoaded = false;
        this.validationSignal = 0;
        this.validationEvaluated = false;
        this.billingProvidersCache = [];
        this.billingProvidersLoaded = false;
        this.inactivePayerIds = [];
        this.inactiveProviderIds = [];
        this.inactiveFacilityIds = [];
        this.preloadProvidersPromise = null;
        this.preloadBillingProvidersPromise = null;
        this.preloadPayersPromise = null;
        this.preloadFacilitiesPromise = null;
    };

    public checkControlNumberUniqueness = async (controlNumber?: string | null): Promise<void> => {
        const value = (controlNumber || '').trim();
        const token = ++this.controlNumberCheckToken;

        if (!value) {
            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = null;
                }
            });
            return;
        }

        if (this.originalControlNumber && value === this.originalControlNumber && !this.isTemplate) {
            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = false;
                }
            });
            return;
        }

        runInAction(() => {
            if (token === this.controlNumberCheckToken) {
                this.isControlNumberUnique = null;
            }
        });

        const runLookup = async (params: any): Promise<boolean> => {
            const api = await this.client<ClaimApi>(ClaimApi);
            const response = this.isSupportApp
                ? await api.listSupportClaims(params)
                : await api.listProviderClaims(params);
            return Array.isArray(response) && response.length > 0;
        };

        try {
            const paramsExternalId = {
                filterExternalId: [`EQ:${value}`],
                filterActive: this.activeFilter,
                limit: 1,
                offset: 0,
            };
            let exists = await runLookup(paramsExternalId);

            if (!exists) {
                const paramsAccountNumber = {
                    filterAccountNumber: [`EQ:${value}`],
                    filterActive: this.activeFilter,
                    limit: 1,
                    offset: 0,
                };
                exists = await runLookup(paramsAccountNumber);
            }

            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = !exists;
                }
            });
        } catch (e) {
            console.warn('checkControlNumberUniqueness failed', e);
            runInAction(() => {
                if (token === this.controlNumberCheckToken) {
                    this.isControlNumberUnique = null;
                }
            });
        }
    };

    private updateIsCreateClaimStatus = (controlNumber?: string | null): void => {
        const hasClaimId = Boolean(this.initialClaimId);
        const baseCreate = !hasClaimId || this.isTemplate;
        const normalizedOriginal = (this.originalControlNumber || '').trim();
        const normalizedCurrent = (controlNumber || '').trim();
        const controlNumberChanged = hasClaimId && !this.isTemplate && normalizedCurrent !== normalizedOriginal;
        this.isCreateClaim = baseCreate || controlNumberChanged;
    };

    public fetchAllProviders = async (): Promise<SupportProvider[]> => {
        try {
            const api = await this.client<ProviderApi>(ProviderApi);
            const limit = 100;
            let allData: SupportProvider[] = [];

            // First page
            const firstResponse = await (this.isSupportApp
                ? api.listSupportProviders({
                      limit,
                      offset: 0,
                      ...this.supportActiveListFilter(),
                  } as any)
                : api.listProviderProviders({
                      limit,
                      offset: 0,
                  } as any));

            allData = allData.concat((firstResponse as any) || []);

            // Continue if full page
            let page = 1;
            let lastLength = (firstResponse || []).length;
            while (lastLength === limit) {
                const offset = page * limit;
                const response = await (this.isSupportApp
                    ? api.listSupportProviders({
                          limit,
                          offset,
                          ...this.supportActiveListFilter(),
                      } as any)
                    : api.listProviderProviders({
                          limit,
                          offset,
                      } as any));

                if (!response || response.length === 0) break;
                allData = allData.concat((response as any) || []);
                lastLength = response.length;
                page++;

                // Safety check to prevent infinite loop
                if (page > 200) break; // max 20,000 records
            }

            // Deduplicate by id
            const deduplicatedData: SupportProvider[] = [];
            const ids = new Set<string>();
            allData.forEach((item: any) => {
                if (item.id && !ids.has(item.id)) {
                    ids.add(item.id);
                    deduplicatedData.push(item);
                }
            });

            return deduplicatedData;
        } catch (e) {
            console.warn('fetchAllProviders failed', e);
            return [];
        }
    };

    public preloadProviders = async (): Promise<void> => {
        if (this.providersLoaded) return;
        if (this.preloadProvidersPromise) {
            return this.preloadProvidersPromise;
        }

        this.preloadProvidersPromise = (async () => {
            try {
                const allProviders = await this.fetchAllProviders();
                runInAction(() => {
                    this.providersCache = allProviders;
                    this.providersLoaded = true;
                });
            } catch (e) {
                console.warn('preloadProviders failed', e);
            } finally {
                this.preloadProvidersPromise = null;
            }
        })();

        return this.preloadProvidersPromise;
    };

    public preloadBillingProviders = async (): Promise<void> => {
        if (this.billingProvidersLoaded) return;
        if (this.preloadBillingProvidersPromise) {
            return this.preloadBillingProvidersPromise;
        }

        this.preloadBillingProvidersPromise = (async () => {
            try {
                // Ensure all providers (including billing) are cached
                await this.preloadProviders();

                const allProviders: any[] = Array.isArray(this.providersCache) ? this.providersCache : [];
                const billingOnly = allProviders.filter(p => String((p as any)?.type).toUpperCase() === 'BILLING');

                runInAction(() => {
                    this.billingProvidersCache = billingOnly;
                    this.billingProvidersLoaded = true;
                });
            } catch (e) {
                console.warn('preloadBillingProviders failed', e);
            } finally {
                this.preloadBillingProvidersPromise = null;
            }
        })();

        return this.preloadBillingProvidersPromise;
    };

    public preloadPayers = async (): Promise<void> => {
        if (this.payersLoaded) return;
        if (this.preloadPayersPromise) {
            return this.preloadPayersPromise;
        }

        this.preloadPayersPromise = (async () => {
            try {
                const allPayers = await this.fetchAllPayers();
                runInAction(() => {
                    this.payersCache = allPayers;
                    this.payersLoaded = true;
                });
            } catch (e) {
                console.warn('preloadPayers failed', e);
            } finally {
                this.preloadPayersPromise = null;
            }
        })();

        return this.preloadPayersPromise;
    };

    public getProviderById = async (providerId: string): Promise<any | null> => {
        if (!providerId) return null;
        const cached = this.providersCache.find(provider => provider.id === providerId);
        if (cached) return cached;

        try {
            const api = await this.client<ProviderApi>(ProviderApi);
            if (this.isSupportApp) {
                return await api.getSupportProvider({ id: providerId } as any);
            }

            // Provider GET /provider/{id} can fail for disabled providers and trigger
            // the global error dialog. Resolve via list + id filter instead.
            const results = await api.listProviderProviders({
                filterId: [`EQ:${providerId}`],
            } as any);
            return results?.[0] || null;
        } catch (e) {
            console.warn('getProviderById failed', e);
            return null;
        }
    };

    public searchPatientsByLastName = async (lastNameQuery: string): Promise<ProviderPatient[]> => {
        if (!lastNameQuery || lastNameQuery.trim().length === 0) {
            return [];
        }

        try {
            const api = await this.client<PatientApi>(PatientApi);
            const params: any = { filterFirstLast: [`LK:${lastNameQuery}%`], filterActive: this.activeFilter, limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportPatients(params)
                : await api.listProviderPatients(params);
            return results || [];
        } catch (e) {
            console.warn('searchPatientsByLastName failed', e);
            return [];
        }
    };

    public searchGuarantorsByLastName = async (lastNameQuery: string): Promise<ProviderGuarantor[]> => {
        if (!lastNameQuery || lastNameQuery.trim().length === 0) {
            return [];
        }

        try {
            const api = await this.client<GuarantorApi>(GuarantorApi);
            const params: any = { filterLast: [`LK:${lastNameQuery}%`], filterActive: this.activeFilter, limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportGuarantors(params)
                : await api.listProviderGuarantors(params);
            return results || [];
        } catch (e) {
            console.warn('searchGuarantorsByLastName failed', e);
            return [];
        }
    };

    /**
     * `null` means the record does not exist (HTTP 404). Any other failure — network, 5xx,
     * auth — is rethrown: callers verify reference ids against these records, and an outage
     * must not read as "no such record" (that would drop a valid id and create a duplicate).
     * The generated client throws the raw `Response` on a non-2xx status.
     */
    private nullIfNotFound = (e: unknown, label: string): null => {
        if (e instanceof Response && e.status === 404) return null;
        console.warn(`${label} failed`, e);
        throw e;
    };

    public getPatientById = async (patientId: string): Promise<ProviderPatient | null> => {
        if (!patientId) return null;
        try {
            const api = await this.client<PatientApi>(PatientApi);
            return this.isSupportApp
                ? await api.getSupportPatient({ id: patientId } as any)
                : await api.getProviderPatient({ id: patientId } as any);
        } catch (e) {
            return this.nullIfNotFound(e, 'getPatientById');
        }
    };

    public getGuarantorById = async (guarantorId: string): Promise<ProviderGuarantor | null> => {
        if (!guarantorId) return null;
        try {
            const api = await this.client<GuarantorApi>(GuarantorApi);
            return this.isSupportApp
                ? await api.getSupportGuarantor({ id: guarantorId } as any)
                : await api.getProviderGuarantor({ id: guarantorId } as any);
        } catch (e) {
            return this.nullIfNotFound(e, 'getGuarantorById');
        }
    };

    public searchSubscribersByName = async (nameQuery: string): Promise<ProviderSubscriber[]> => {
        if (!nameQuery || nameQuery.trim().length === 0) {
            return [];
        }
        try {
            const api = await this.client<SubscriberApi>(SubscriberApi);
            const params: any = { filterFirstLast: [`LK:${nameQuery}%`], filterActive: this.activeFilter, limit: 10 };
            const results = this.isSupportApp
                ? await api.listSupportSubscribers(params)
                : await api.listProviderSubscribers(params);
            return results || [];
        } catch (e) {
            console.warn('searchSubscribersByName failed', e);
            return [];
        }
    };

    public getSubscriberById = async (subscriberId: string): Promise<ProviderSubscriber | null> => {
        if (!subscriberId) return null;
        try {
            const api = await this.client<SubscriberApi>(SubscriberApi);
            return this.isSupportApp
                ? await api.getSupportSubscriber({ id: subscriberId } as any)
                : await api.getProviderSubscriber({ id: subscriberId } as any);
        } catch (e) {
            return this.nullIfNotFound(e, 'getSubscriberById');
        }
    };

    /**
     * The preloaded payer list, for callers that filter it themselves.
     *
     * Exposed because the claim-editor payer lookup was reaching in with
     * `(store as any)['payersCache']`, which type-checks, silently survives a
     * rename, and hides a real dependency from every tool that looks for one.
     */
    public get payers(): AnyPayer[] {
        return this.payersCache;
    }

    public get arePayersLoaded(): boolean {
        return this.payersLoaded;
    }

    /**
     * The preloaded BILLING-only provider list, for callers that filter it themselves.
     *
     * Same reason as `payers`: the billing-provider lookup was reading
     * `store['billingProvidersLoaded']` / `(store as any)['billingProvidersCache']`.
     */
    public get billingProviders(): any[] {
        return this.billingProvidersCache;
    }

    public get areBillingProvidersLoaded(): boolean {
        return this.billingProvidersLoaded;
    }

    public get areProvidersLoaded(): boolean {
        return this.providersLoaded;
    }

    /**
     * Server-side payer search. Currently UNUSED — kept because it is the right
     * shape for a future move off the full preload.
     *
     * Note before adopting it: `LK:${q}%` is a PREFIX match, so "blue" finds
     * "Blue Shield…" but NOT "Anthem BCBS California". The lookup filters the
     * preloaded list with `includes` instead, which is why it matches mid-name.
     * Swapping to this without changing the filter would silently narrow what
     * operators can find.
     */
    public searchPayersByName = async (nameQuery: string): Promise<AnyPayer[]> => {
        if (!nameQuery || nameQuery.trim().length === 0) {
            return [];
        }
        try {
            const api = await this.client<PayerApi>(PayerApi);
            const params: any = {
                filterName: [`LK:${nameQuery}%`],
                limit: 10,
                ...this.supportActiveListFilter(),
            };
            const results = this.isSupportApp
                ? await api.listSupportPayers(params)
                : await api.listProviderPayers(params);
            return results || [];
        } catch (e) {
            console.warn('searchPayersByName failed', e);
            return [];
        }
    };

    public getPayerById = async (payerId: string): Promise<AnyPayer | null> => {
        if (!payerId) return null;

        const cached = this.payersCache.find(p => p.id === payerId);
        if (cached) return cached;

        try {
            const api = await this.client<PayerApi>(PayerApi);
            if (this.isSupportApp) {
                return await api.getSupportPayer({ id: payerId } as any);
            }

            // Provider GET /payer/{id} fails for disabled payers and triggers the global
            // error dialog, which previously left the claim-editor payer select unusable.
            // Resolve via list + id filter so a missing/disabled payer returns [] instead.
            const results = await api.listProviderPayers({
                filterId: [`EQ:${payerId}`],
            } as any);
            return results?.[0] || null;
        } catch (e) {
            console.warn('getPayerById failed', e);
            return null;
        }
    };

    public searchFacilities = async (nameQuery?: string, facilityId?: string): Promise<any[]> => {
        const hasNameQuery = Boolean(nameQuery && nameQuery.trim().length > 0);
        const hasFacilityId = Boolean(facilityId && facilityId.trim().length > 0);

        // The unfiltered default list (no name, no id) is identical every time it's asked
        // for — share/cache it instead of hitting the API once per mounted facility select.
        if (!hasNameQuery && !hasFacilityId) {
            return this.preloadFacilities();
        }

        try {
            const api = await this.client<FacilityApi>(FacilityApi);
            // Apply a reasonable default limit to avoid returning an unbounded number of facilities
            const params: any = { limit: 50, ...this.supportActiveListFilter() };

            if (hasNameQuery) {
                params.filterName = [`LK:${nameQuery}%`];
            }
            if (hasFacilityId) {
                params.filterId = [`EQ:${facilityId}`];
            }

            const results = this.isSupportApp
                ? await api.listSupportFacilities(params)
                : await api.listProviderFacilities(params);
            return results || [];
        } catch (err) {
            console.warn('searchFacilities failed', err);
            return [];
        }
    };

    /**
     * The facility with this id, or `null` when the list has no such (active) facility.
     * Unlike `searchFacilities`, request failures are rethrown rather than read as an empty
     * list — see `nullIfNotFound` for why a verifier must tell the two apart.
     */
    public getFacilityById = async (facilityId: string): Promise<any | null> => {
        if (!facilityId) return null;
        try {
            const api = await this.client<FacilityApi>(FacilityApi);
            const params: any = { limit: 50, filterId: [`EQ:${facilityId}`], ...this.supportActiveListFilter() };
            const results = this.isSupportApp
                ? await api.listSupportFacilities(params)
                : await api.listProviderFacilities(params);
            return (results || []).find((f: any) => f.id === facilityId) ?? null;
        } catch (e) {
            return this.nullIfNotFound(e, 'getFacilityById');
        }
    };

    private preloadFacilities = async (): Promise<any[]> => {
        if (this.facilitiesLoaded) return this.facilitiesCache;
        if (this.preloadFacilitiesPromise) return this.preloadFacilitiesPromise;

        this.preloadFacilitiesPromise = (async () => {
            try {
                const api = await this.client<FacilityApi>(FacilityApi);
                const params: any = { limit: 50, ...this.supportActiveListFilter() };
                const results = this.isSupportApp
                    ? await api.listSupportFacilities(params)
                    : await api.listProviderFacilities(params);
                runInAction(() => {
                    this.facilitiesCache = results || [];
                    this.facilitiesLoaded = true;
                });
                return this.facilitiesCache;
            } catch (err) {
                console.warn('preloadFacilities failed', err);
                return [];
            } finally {
                this.preloadFacilitiesPromise = null;
            }
        })();

        return this.preloadFacilitiesPromise;
    };
}
